import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';
import { InternalLink } from '@components/Link';
import { accountActions, authActions, alertActions } from '@actions';
import { authService } from '@services';
import { Input } from '@components/Input';
import { Select } from '@components/Select';
import { Switch } from '@components/Switch';
import Tooltip from '@components/Tooltip';
import PhoneInput from '@components/PhoneInput';
import Button from '@components/Button';
import { Typography } from '@components/Typography';
import ImageUploaderSingle from '@components/ImageUploaderSingle';
import { KB } from '@constants/file';
import { LANGUAGE } from '@constants/language';
import { regexAnyCharAtLeastOneNumberAndLetter } from '@constants/regex';
import { RoutePath } from '@constants/routes';
import GetAppRoundedIcon from '@mui/icons-material/GetAppRounded';
import { useTranslation, useYupValidationResolver, useWebPushNotifications, useInstallApp } from '@hooks';
import { preparePhoneNumberForSubmit, setInitialPhoneNumberToForm } from '@helpers/phone';
import { Container, Footer, Content } from './styles';

const EmailNotificationsFrequency = {
    EveryHour: '1',
    EveryDay: '2',
};

const PerformanceReportsFrequency = {
    EveryWeek: '1',
    EveryMonth: '2',
};

const PersonalSettingsPage = () => {
    const { t } = useTranslation();

    const user = useSelector(state => state.auth.user);
    const account = useSelector(state => state.account?.account);

    const dispatch = useDispatch();
    const { isSubscribed, isSupported, togglePermissions } = useWebPushNotifications();
    const { platform, isInstalled, canInstall, install } = useInstallApp();
    const [isUploading, setIsUploading] = useState(false);

    const onUploadProfileImage = async e => {
        const file = e.target.files[0];
        if (!file) return;
        setIsUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            const updatedUser = await authService.uploadProfileImage(formData);
            dispatch(authActions.patchUser({ profileImageUrl: updatedUser.profileImageUrl }));
            await dispatch(accountActions.get());
            dispatch(alertActions.success(t('alertMessages.updateSuccess')));
        } catch (_) {
            dispatch(alertActions.error(t('alertMessages.updateFail')));
        } finally {
            setIsUploading(false);
        }
    };

    const onDeleteProfileImage = async () => {
        setIsUploading(true);
        try {
            await authService.deleteProfileImage();
            dispatch(authActions.patchUser({ profileImageUrl: null }));
            await dispatch(accountActions.get());
            dispatch(alertActions.success(t('alertMessages.updateSuccess')));
        } catch (_) {
            dispatch(alertActions.error(t('alertMessages.updateFail')));
        } finally {
            setIsUploading(false);
        }
    };

    const PersonalSettingsFormSchema = yup.object().shape(
        {
            firstName: yup.string().required('validation.required'),
            lastName: yup.string().required('validation.required'),
            phoneNumber: yup.string().validatePhone('validation.wrongPhoneFormat', true),
            currentPassword: yup.string().when('newPassword', {
                is: newPassword => newPassword?.length > 0,
                then: schema => schema.required('validation.required'),
            }),
            newPassword: yup.string().when('currentPassword', {
                is: currentPassword => currentPassword?.length > 0,
                then: schema =>
                    schema
                        .required('validation.required')
                        .min(8, 'validation.minPasswordLength')
                        .matches(regexAnyCharAtLeastOneNumberAndLetter, 'validation.wrongPasswordFormat'),
            }),
            confirmPassword: yup.string().when('newPassword', {
                is: newPassword => newPassword?.length > 0,
                then: schema =>
                    schema.required('validation.required').sameAs('newPassword', 'validation.confirmPasswordMatch'),
            }),
        },
        [['currentPassword', 'newPassword', 'confirmPassword']],
    );

    const resolver = useYupValidationResolver(PersonalSettingsFormSchema, () => ({
        dirtyFields,
        touchedFields,
    }));

    const {
        getValues,
        handleSubmit,
        setValue,
        control,
        reset,
        watch,
        formState: { errors, isValid, isDirty, isSubmitting, dirtyFields, touchedFields },
    } = useForm({
        mode: 'all',
        resolver,
        defaultValues: {
            id: '',
            firstName: '',
            lastName: '',
            email: '',
            phoneNumber: '',
            systemLanguage: '',
            currentPassword: '',
            newPassword: '',
            confirmPassword: '',
            emailNotificationsEnabled: false,
            emailNotificationsFrequency: EmailNotificationsFrequency.EveryHour,
            profileSyncConflictsEnabled: false,
            newReviewsEnabled: false,
            newMessagesEnabled: false,
            surveyResponsesEnabled: false,
            performanceReportsEnabled: false,
            performanceReportsFrequency: PerformanceReportsFrequency.EveryWeek,
            negativeFeedbacksAlerts: false,
        },
    });

    useEffect(() => {
        if (user) {
            setValue('id', user?.id || '', { shouldValidate: true });
            setValue('firstName', user?.firstName || '', { shouldValidate: true });
            setValue('lastName', user?.lastName || '', { shouldValidate: true });
            setValue('email', user?.email || '', { shouldValidate: true });
            setValue('phoneNumber', setInitialPhoneNumberToForm(user?.phoneNumber, account?.locale), {
                shouldValidate: true,
                shouldTouch: true,
            });
            setValue('systemLanguage', user?.language || '', { shouldValidate: true });
            setValue('emailNotificationsEnabled', user?.emailNotificationsEnabled || false);
            setValue(
                'emailNotificationsFrequency',
                user?.emailNotificationsFrequency || EmailNotificationsFrequency.EveryHour,
            );
            setValue('profileSyncConflictsEnabled', user?.profileSyncConflictsEnabled || false);
            setValue('newReviewsEnabled', user?.newReviewsEnabled || false);
            setValue('newMessagesEnabled', user?.newMessagesEnabled || false);
            setValue('surveyResponsesEnabled', user?.surveyResponsesEnabled || false);
            setValue('performanceReportsEnabled', user?.performanceReportsEnabled || false);
            setValue(
                'performanceReportsFrequency',
                user?.performanceReportsFrequency || PerformanceReportsFrequency.EveryWeek,
            );
            setValue('negativeFeedbacksAlerts', user?.negativeFeedbacksAlerts || false);
        }
    }, [user, account?.locale]);

    const onSubmit = async () => {
        const values = getValues();
        try {
            const updatedUser = await authService.updatePersonalSettings({
                ...values,
                emailNotificationsFrequency: Number(values.emailNotificationsFrequency),
                performanceReportsFrequency: Number(values.performanceReportsFrequency),
                negativeFeedbacksAlerts: values.phoneNumber ? values.negativeFeedbacksAlerts : false,
                phoneNumber: preparePhoneNumberForSubmit(values.phoneNumber, true),
            });
            dispatch(authActions.setUser(updatedUser));
            await dispatch(accountActions.get());
            dispatch(alertActions.success(t('alertMessages.updateSuccess')));
            reset(values);
            setValue('negativeFeedbacksAlerts', updatedUser.negativeFeedbacksAlerts, { shouldDirty: false });
            setValue('currentPassword', '', { shouldDirty: false });
            setValue('newPassword', '', { shouldDirty: false });
            setValue('confirmPassword', '', { shouldDirty: false });
        } catch (_) {
            dispatch(alertActions.error(t('alertMessages.updateFail')));
        }
    };

    const phoneNumberInput = watch('phoneNumber');
    const performanceReportsEnabled = watch('performanceReportsEnabled');
    const emailNotificationsEnabled = watch('emailNotificationsEnabled');

    return (
        <Container>
            <Typography variant="h6" sx={{ marginBottom: '24px', color: 'var(--text-primary)' }}>
                {t('SettingsProfile.generalInfo')}
            </Typography>
            <Content>
                <Controller
                    control={control}
                    name="firstName"
                    render={({ field, fieldState: { error }, ref }) => (
                        <Input
                            ref={ref}
                            inputProps={{ ...field }}
                            label={t('common.firstName')}
                            fullWidth
                            required
                            error={!!error}
                            helperText={error ? t(error.message) : null}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="lastName"
                    render={({ field, fieldState: { error }, ref }) => (
                        <Input
                            ref={ref}
                            inputProps={{ ...field }}
                            label={t('common.lastName')}
                            fullWidth
                            required
                            error={!!error}
                            helperText={error ? t(error.message) : null}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="email"
                    render={({ field, ref }) => (
                        <Input ref={ref} inputProps={{ ...field }} label={t('common.email')} disabled />
                    )}
                />
                <Controller
                    control={control}
                    name="phoneNumber"
                    render={({ field, fieldState: { error } }) => (
                        <PhoneInput
                            placeholder=""
                            label={t('common.phoneNumber')}
                            inputProps={{ name: 'phoneNumber' }}
                            value={field.value}
                            autoFormat
                            onChange={value => {
                                setValue('phoneNumber', value, {
                                    shouldValidate: true,
                                    shouldDirty: true,
                                    shouldTouch: true,
                                });
                            }}
                            error={error ? t(error.message) : null}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="systemLanguage"
                    render={({ field, ref }) => (
                        <Select
                            {...field}
                            value={getValues('systemLanguage')}
                            ref={ref}
                            label={t('SettingsProfile.systemLanguage')}
                            options={[
                                { value: LANGUAGE.ENGLISH, label: 'English (US)' },
                                { value: LANGUAGE.UKRAINIAN, label: 'Ukrainian (UA)' },
                                { value: LANGUAGE.PORTUGUESE, label: 'Portuguese (PT)' },
                                { value: LANGUAGE.SPANISH, label: 'Spanish (ES)' },
                            ]}
                        />
                    )}
                />
            </Content>
            <Typography variant="h6" sx={{ marginBottom: '16px', marginTop: '24px', color: 'var(--text-primary)' }}>
                {t('SettingsProfile.profilePhoto')}
            </Typography>
            <ImageUploaderSingle
                maxSize={KB * 500}
                minSize={10 * KB}
                extensions="image/*"
                cropConfig={{ aspectRatio: 1, outputWidth: 250 }}
                logoUrl={user?.profileImageUrl}
                disabled={isUploading}
                uploadLogo={onUploadProfileImage}
                onDelete={onDeleteProfileImage}
                isUploading={isUploading}
            />
            <Typography variant="h6" sx={{ marginBottom: '24px', marginTop: '24px', color: 'var(--text-primary)' }}>
                {t('common.password')}
            </Typography>
            <Content isPassword>
                <Controller
                    control={control}
                    name="currentPassword"
                    render={({ field, fieldState: { error }, ref }) => (
                        <Input
                            ref={ref}
                            inputProps={{ ...field }}
                            type="password"
                            sx={{ gridArea: 'area1' }}
                            label={t('SettingsProfile.currentPassword')}
                            fullWidth
                            error={!!error}
                            helperText={error ? t(error.message) : null}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="newPassword"
                    render={({ field, fieldState: { error }, ref }) => (
                        <Input
                            ref={ref}
                            inputProps={{ ...field }}
                            type="password"
                            sx={{ gridArea: 'area2' }}
                            label={t('SettingsProfile.newPassword')}
                            fullWidth
                            error={!!error}
                            helperText={error ? t(error.message) : null}
                        />
                    )}
                />
                <Typography
                    variant="body2"
                    sx={{
                        marginTop: '12px',
                        gridArea: 'area3',
                        color: 'var(--text-primary)',
                        '@media (max-width: 1024px)': {
                            marginTop: '-16px',
                        },
                    }}
                >
                    {t('SettingsProfile.passwordResetText')}{' '}
                    <InternalLink to={RoutePath.ForgotPassword} underline="hover">
                        {t('SettingsProfile.passwordResetUrl')}
                    </InternalLink>
                </Typography>
                <Controller
                    control={control}
                    name="confirmPassword"
                    render={({ field, fieldState: { error }, ref }) => (
                        <Input
                            ref={ref}
                            inputProps={{ ...field }}
                            type="password"
                            sx={{ gridArea: 'area4' }}
                            label={t('SettingsProfile.confirmPassword')}
                            fullWidth
                            error={!!error}
                            helperText={error ? t(error.message) : null}
                        />
                    )}
                />
            </Content>
            <Typography variant="h6" sx={{ marginBottom: '24px', marginTop: '24px', color: 'var(--text-primary)' }}>
                {t('SettingsProfile.alertsAndReports')}
            </Typography>
            <Content>
                <Controller
                    control={control}
                    name="emailNotificationsEnabled"
                    render={({ field, ref }) => (
                        <div>
                            <Switch
                                {...field}
                                ref={ref}
                                checked={getValues('emailNotificationsEnabled')}
                                handleChange={event => {
                                    setValue('emailNotificationsEnabled', event.target.checked, {
                                        shouldDirty: true,
                                    });
                                    if (!event.target.checked) {
                                        setValue('emailNotificationsFrequency', EmailNotificationsFrequency.EveryHour, {
                                            shouldDirty: true,
                                        });
                                        setValue('profileSyncConflictsEnabled', false, { shouldDirty: true });
                                        setValue('newReviewsEnabled', false, { shouldDirty: true });
                                        setValue('newMessagesEnabled', false, { shouldDirty: true });
                                        setValue('surveyResponsesEnabled', false, { shouldDirty: true });
                                    }
                                }}
                                label={t('SettingsProfile.emailNotifications')}
                            />
                            <Typography variant="caption" sx={{ color: 'var(--text-secondary)', display: 'block' }}>
                                {t('SettingsProfile.emailNotificationsDescription')}
                            </Typography>
                        </div>
                    )}
                />
                {emailNotificationsEnabled ? (
                    <>
                        <Controller
                            control={control}
                            name="emailNotificationsFrequency"
                            render={({ field, ref }) => (
                                <Select
                                    {...field}
                                    value={getValues('emailNotificationsFrequency')}
                                    ref={ref}
                                    label={t('common.frequency')}
                                    options={[
                                        {
                                            value: EmailNotificationsFrequency.EveryHour,
                                            label: t('SettingsProfile.everyHour'),
                                        },
                                        {
                                            value: EmailNotificationsFrequency.EveryDay,
                                            label: t('SettingsProfile.everyDay'),
                                        },
                                    ]}
                                />
                            )}
                        />
                        <Controller
                            control={control}
                            name="profileSyncConflictsEnabled"
                            render={({ field, ref }) => (
                                <Switch
                                    {...field}
                                    ref={ref}
                                    checked={getValues('profileSyncConflictsEnabled')}
                                    handleChange={event =>
                                        setValue('profileSyncConflictsEnabled', event.target.checked, {
                                            shouldDirty: true,
                                        })
                                    }
                                    label={t('SettingsProfile.profileSyncConflicts')}
                                />
                            )}
                        />
                        <Controller
                            control={control}
                            name="newReviewsEnabled"
                            render={({ field, ref }) => (
                                <Switch
                                    {...field}
                                    ref={ref}
                                    checked={getValues('newReviewsEnabled')}
                                    handleChange={event =>
                                        setValue('newReviewsEnabled', event.target.checked, {
                                            shouldDirty: true,
                                        })
                                    }
                                    label={t('SettingsProfile.newReviews')}
                                />
                            )}
                        />
                        <Controller
                            control={control}
                            name="newMessagesEnabled"
                            render={({ field, ref }) => (
                                <Switch
                                    {...field}
                                    ref={ref}
                                    checked={getValues('newMessagesEnabled')}
                                    handleChange={event =>
                                        setValue('newMessagesEnabled', event.target.checked, {
                                            shouldDirty: true,
                                        })
                                    }
                                    label={t('SettingsProfile.newMessages')}
                                />
                            )}
                        />
                        <Controller
                            control={control}
                            name="surveyResponsesEnabled"
                            render={({ field, ref }) => (
                                <Switch
                                    {...field}
                                    ref={ref}
                                    checked={getValues('surveyResponsesEnabled')}
                                    handleChange={event =>
                                        setValue('surveyResponsesEnabled', event.target.checked, {
                                            shouldDirty: true,
                                        })
                                    }
                                    label={t('SettingsProfile.newSurveyResponses')}
                                />
                            )}
                        />
                    </>
                ) : (
                    <div />
                )}
                <Controller
                    control={control}
                    name="performanceReportsEnabled"
                    render={({ field, ref }) => (
                        <div>
                            <Switch
                                {...field}
                                ref={ref}
                                checked={getValues('performanceReportsEnabled')}
                                handleChange={event => {
                                    setValue('performanceReportsEnabled', event.target.checked, {
                                        shouldDirty: true,
                                    });
                                    if (!event.target.checked) {
                                        setValue('performanceReportsFrequency', PerformanceReportsFrequency.EveryWeek, {
                                            shouldDirty: true,
                                        });
                                    }
                                }}
                                label={t('SettingsProfile.performanceReports')}
                            />
                            <Typography variant="caption" sx={{ color: 'var(--text-secondary)', display: 'block' }}>
                                {t('SettingsProfile.performanceReportsDescription')}
                            </Typography>
                        </div>
                    )}
                />
                {performanceReportsEnabled ? (
                    <Controller
                        control={control}
                        name="performanceReportsFrequency"
                        render={({ field, ref }) => (
                            <Select
                                {...field}
                                value={getValues('performanceReportsFrequency')}
                                ref={ref}
                                label={t('common.frequency')}
                                options={[
                                    {
                                        value: PerformanceReportsFrequency.EveryWeek,
                                        label: t('SettingsProfile.everyWeek'),
                                    },
                                    {
                                        value: PerformanceReportsFrequency.EveryMonth,
                                        label: t('SettingsProfile.everyMonth'),
                                    },
                                ]}
                            />
                        )}
                    />
                ) : (
                    <div />
                )}
                {phoneNumberInput && !errors?.phoneNumber ? (
                    <Controller
                        control={control}
                        name="negativeFeedbacksAlerts"
                        render={({ field, ref }) => (
                            <div>
                                <Switch
                                    {...field}
                                    ref={ref}
                                    checked={getValues('negativeFeedbacksAlerts')}
                                    handleChange={event =>
                                        setValue('negativeFeedbacksAlerts', event.target.checked, {
                                            shouldDirty: true,
                                        })
                                    }
                                    label={t('SettingsProfile.smsNotifications')}
                                />
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: 'var(--text-secondary)',
                                        display: 'block',
                                    }}
                                >
                                    {t('SettingsProfile.smsNotificationsDescription')}
                                </Typography>
                            </div>
                        )}
                    />
                ) : (
                    <Tooltip placement="top" title={t('SettingsProfile.emptyNumberTooltip')}>
                        <span>
                            <Controller
                                control={control}
                                name="negativeFeedbacksAlerts"
                                render={({ field, ref }) => (
                                    <Switch
                                        {...field}
                                        ref={ref}
                                        disabled
                                        checked={getValues('negativeFeedbacksAlerts')}
                                        label={t('SettingsProfile.smsNotifications')}
                                    />
                                )}
                            />
                        </span>
                    </Tooltip>
                )}
                <div />
                {isSupported && (
                    <div>
                        <Switch
                            checked={isSubscribed}
                            handleChange={togglePermissions}
                            label={t('SettingsProfile.browserNotifications')}
                        />
                        <Typography
                            variant="caption"
                            sx={{
                                color: 'var(--text-secondary)',
                                display: 'block',
                            }}
                        >
                            {t('SettingsProfile.browserNotificationsDescription')}
                        </Typography>
                    </div>
                )}
            </Content>
            {(canInstall || (isInstalled && platform !== 'ios') || (platform === 'ios' && !isInstalled)) && (
                <>
                    <Typography
                        variant="h6"
                        sx={{ marginBottom: '8px', marginTop: '24px', color: 'var(--text-primary)' }}
                    >
                        {t('SettingsProfile.installApp')}
                    </Typography>
                    <Typography variant="body2" sx={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
                        {platform === 'ios'
                            ? t('SettingsProfile.installIosInstructions')
                            : t('SettingsProfile.installAppDescription')}
                    </Typography>
                    {platform !== 'ios' && (
                        <Button
                            variant="outlined"
                            startIcon={<GetAppRoundedIcon />}
                            onClick={install}
                            disabled={isInstalled}
                        >
                            {isInstalled ? t('SettingsProfile.installed') : t('SettingsProfile.installApp')}
                        </Button>
                    )}
                </>
            )}
            <Footer>
                <Button
                    onClick={handleSubmit(onSubmit)}
                    variant="contained"
                    disabled={isSubmitting || !isValid || !isDirty}
                    sx={{ '@media (max-width: 1024px)': { width: '100%' } }}
                >
                    {t('buttons.saveChanges')}
                </Button>
            </Footer>
        </Container>
    );
};

export default PersonalSettingsPage;
